/**
 * The world clock.
 *
 * One in-game day is four real hours, so six of them pass in a real day and a player who only
 * ever plays the same evening slot still sees every hour of the world. The clock is a pure
 * function of wall time — `timeOfDay(Date.now())` — with no state, no server message and no
 * client drift correction, because there is nothing to drift: two machines whose clocks agree
 * to the second agree on the sky to a hundredth of a degree, and one that does not is wrong by
 * exactly the amount its clock is wrong. Nothing in the sim reads this; it drives the sky, the
 * lantern lighting and the quest text that mentions the hour.
 *
 * Phase 0 is midnight, 0.5 is noon. The sun is above the horizon from `SUNRISE_T` to `SUNSET_T`,
 * which is deliberately more than half the cycle: night is atmosphere, not a 96-minute wait for
 * the light to come back. Forty-three minutes of true dark in every four hours, with a moon and
 * lit lanterns, is the shape Chris asked for — playable, not blind.
 */

/** One in-game day in real milliseconds. Four hours; six in-game days per real day. */
export const DAY_MS = 4 * 60 * 60 * 1000;

/** Phase at which the sun crosses the horizon going up. */
export const SUNRISE_T = 0.14;
/** Phase at which the sun crosses the horizon going down. */
export const SUNSET_T = 0.86;
/**
 * Half-width of the sunrise and sunset *phases* — the colour events, which start before the sun
 * is up and end after it is. The sun itself crosses at the two constants above.
 */
export const TWILIGHT_T = 0.05;

/** Peak sun elevation, at noon, in radians. */
export const MAX_SUN_ELEVATION = (62 * Math.PI) / 180;
/** How far below the horizon the sun sits at midnight, in radians. */
export const MIN_SUN_ELEVATION = (-40 * Math.PI) / 180;

/** Compass bearing of the sun at sunrise and at sunset, in radians, measured from north. */
const AZIMUTH_SUNRISE = (100 * Math.PI) / 180;
const AZIMUTH_SUNSET = (260 * Math.PI) / 180;

export type DayPhase = 'sunrise' | 'day' | 'sunset' | 'night';

/** Fraction of the cycle the sun spends above the horizon. */
const DAY_SPAN = SUNSET_T - SUNRISE_T;
/** …and below it. */
const NIGHT_SPAN = 1 - DAY_SPAN;

/**
 * Where in the in-game day a wall-clock instant falls, in `[0, 1)`.
 *
 * Negative and non-integer inputs are handled: the modulo is the maths one, not JavaScript's
 * remainder, so a timestamp before the epoch does not come back negative and put the sun under
 * the map.
 */
export function timeOfDay(unixMs: number): number {
  if (!Number.isFinite(unixMs)) return 0;
  const t = (unixMs % DAY_MS) / DAY_MS;
  return t < 0 ? t + 1 : t;
}

/** Which in-game day this instant belongs to, counting from the epoch. Stable across machines. */
export function dayIndex(unixMs: number): number {
  return Math.floor(unixMs / DAY_MS);
}

/**
 * Sun elevation above the horizon in radians for a phase in `[0, 1)`.
 *
 * Two half-sines welded at the horizon crossings rather than one sine over the whole cycle: the
 * day is longer than the night, so a single sine would have to be asymmetric anyway, and this
 * form is continuous, is exactly zero at sunrise and sunset, and peaks exactly at the two
 * midpoints. The derivative has a kink at the horizon, which nothing can see.
 */
export function sunElevation(t: number): number {
  const p = wrap(t);
  if (p >= SUNRISE_T && p < SUNSET_T) {
    return MAX_SUN_ELEVATION * Math.sin((Math.PI * (p - SUNRISE_T)) / DAY_SPAN);
  }
  // Night, wrapped so the arc runs continuously from sunset through midnight to sunrise.
  const since = p >= SUNSET_T ? p - SUNSET_T : p + (1 - SUNSET_T);
  return MIN_SUN_ELEVATION * Math.sin((Math.PI * since) / NIGHT_SPAN);
}

/**
 * Sun bearing in radians. Sweeps from `AZIMUTH_SUNRISE` to `AZIMUTH_SUNSET` across the day and
 * carries on the same way round through the night, so the moon (which the sky draws opposite the
 * sun) tracks the other side of the sky rather than reversing at midnight.
 */
export function sunAzimuth(t: number): number {
  const p = wrap(t);
  if (p >= SUNRISE_T && p < SUNSET_T) {
    const u = (p - SUNRISE_T) / DAY_SPAN;
    return AZIMUTH_SUNRISE + (AZIMUTH_SUNSET - AZIMUTH_SUNRISE) * u;
  }
  const since = p >= SUNSET_T ? p - SUNSET_T : p + (1 - SUNSET_T);
  const u = since / NIGHT_SPAN;
  return AZIMUTH_SUNSET + (AZIMUTH_SUNRISE + 2 * Math.PI - AZIMUTH_SUNSET) * u;
}

/**
 * Unit vector pointing *toward* the sun, in the client's world axes (+y up, yaw 0 down -z).
 * Writes into `out` and returns it, so the render loop allocates nothing.
 */
export function sunDirection(
  t: number,
  out: { x: number; y: number; z: number },
): {
  x: number;
  y: number;
  z: number;
} {
  const el = sunElevation(t);
  const az = sunAzimuth(t);
  const c = Math.cos(el);
  out.x = c * Math.sin(az);
  out.y = Math.sin(el);
  out.z = -c * Math.cos(az);
  return out;
}

/** Which of the four named parts of the day a phase falls in. */
export function phase(t: number): DayPhase {
  const p = wrap(t);
  if (p >= SUNRISE_T - TWILIGHT_T && p < SUNRISE_T + TWILIGHT_T) return 'sunrise';
  if (p >= SUNRISE_T + TWILIGHT_T && p < SUNSET_T - TWILIGHT_T) return 'day';
  if (p >= SUNSET_T - TWILIGHT_T && p < SUNSET_T + TWILIGHT_T) return 'sunset';
  return 'night';
}

/**
 * How dark it is, `0` at full day and `1` at the darkest point of the night, eased.
 *
 * The sky ramp reads this rather than the elevation directly, because the interesting range is
 * the twenty degrees either side of the horizon and everything above that looks the same.
 * Lanterns, the moon and the night grade all key off this one number so they cannot disagree.
 */
export function nightFactor(t: number): number {
  const el = sunElevation(t);
  const HORIZON_BAND = (12 * Math.PI) / 180;
  const k = clamp01((HORIZON_BAND - el) / (2 * HORIZON_BAND));
  return k * k * (3 - 2 * k);
}

/**
 * `0` away from the horizon and `1` at the moment of crossing it — how much sunrise/sunset
 * colour to mix in. Symmetric, so one number drives both events.
 */
export function twilightFactor(t: number): number {
  const el = sunElevation(t);
  const BAND = (10 * Math.PI) / 180;
  const k = clamp01(1 - Math.abs(el) / BAND);
  return k * k * (3 - 2 * k);
}

/**
 * The hour said the way the Choir says it.
 *
 * Lanternfall has no clock tower and the Wakers have no watches, so the HUD does not print
 * `14:32` at somebody — it names the hour. Eight phrases over the four-hour day, each one an
 * answer to "when is it" that a person standing in the street would actually give.
 *
 * Every boundary is derived from `SUNRISE_T`, `SUNSET_T` and `TWILIGHT_T` rather than typed as a
 * number, so moving the sun moves the words with it and the phrase can never claim first light
 * while the sky is still black.
 */
export type DayPhrase =
  | 'deep night'
  | 'the last watch'
  | 'first light'
  | 'the long morning'
  | 'high sun'
  | 'the low sun'
  | 'vespers'
  | 'lampfall';

/** Half-width of the "high sun" band around noon. */
const NOON_BAND = 0.08;

/**
 * Band starts, ascending. The first band is entered by wrapping past the last, which is how
 * `deep night` covers midnight without being written down twice.
 */
const PHRASE_BANDS: readonly (readonly [number, DayPhrase])[] = [
  [SUNRISE_T - 2 * TWILIGHT_T, 'the last watch'],
  [SUNRISE_T - TWILIGHT_T, 'first light'],
  [SUNRISE_T + TWILIGHT_T, 'the long morning'],
  [0.5 - NOON_BAND, 'high sun'],
  [0.5 + NOON_BAND, 'the low sun'],
  [SUNSET_T - TWILIGHT_T, 'vespers'],
  [SUNSET_T + TWILIGHT_T, 'lampfall'],
  [SUNSET_T + 2 * TWILIGHT_T, 'deep night'],
];

/** What to call the hour at phase `t`. */
export function dayPhrase(t: number): DayPhrase {
  const p = wrap(t);
  let out: DayPhrase = PHRASE_BANDS[PHRASE_BANDS.length - 1]?.[1] ?? 'deep night';
  for (const [start, name] of PHRASE_BANDS) {
    if (p < start) break;
    out = name;
  }
  return out;
}

/**
 * Which glyph the clock wears: the sun while it is above the horizon, the moon while it is not.
 * Keyed off `sunElevation` rather than the phrase, so the badge and the sky can never disagree.
 */
export function clockGlyph(t: number): 'sun' | 'moon' {
  return sunElevation(t) > 0 ? 'sun' : 'moon';
}

/** The in-game hour, `0..24`, for anything that prints a clock. */
export function clockHours(t: number): number {
  return wrap(t) * 24;
}

/** `HH:MM` in in-game hours. */
export function clockText(t: number): string {
  const h = clockHours(t);
  const hh = Math.floor(h);
  const mm = Math.floor((h - hh) * 60);
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function wrap(t: number): number {
  if (!Number.isFinite(t)) return 0;
  const p = t % 1;
  return p < 0 ? p + 1 : p;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
